import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { getTranslationsState, getUserV3State } from '@redux/selectors';

import { UserV3 } from '@wegoTypes/userV3';

import styles from './ProfileSetupProgress.module.scss';

interface Step {
  id: number;
  isCompleted: (userV3: UserV3 | null) => boolean;
}

const PROFILE_SETUP_STEPS: Step[] = [
  {
    id: 1,
    isCompleted: (userV3) => userV3 !== null,
  },
  {
    id: 2,
    isCompleted: (userV3) => {
      const { profile, primaryTraveller } = userV3 || {};
      return !!(
        primaryTraveller?.gender &&
        profile?.firstName &&
        profile?.lastName &&
        profile?.nationality &&
        primaryTraveller?.dateOfBirth
      );
    },
  },
  {
    id: 3,
    isCompleted: (userV3) => {
      const { primaryTraveller } = userV3 || {};
      return !!(
        primaryTraveller?.travellerDocuments && primaryTraveller.travellerDocuments.length > 0
      );
    },
  },
];

const totalSteps = PROFILE_SETUP_STEPS.length;

const ProfileSetupProgress: React.FC = () => {
  const userV3 = useSelector(getUserV3State);
  const translations = useSelector(getTranslationsState);

  const { progressPercentage, remainingSteps, hideComponent } = useMemo(() => {
    if (!userV3) {
      return {
        progressPercentage: 0,
        remainingSteps: totalSteps,
        hideComponent: true,
      };
    }
    const completedSteps = PROFILE_SETUP_STEPS.filter((step) => step.isCompleted(userV3)).length;
    return {
      progressPercentage: (completedSteps / totalSteps) * 100,
      remainingSteps: totalSteps - completedSteps,
      hideComponent: completedSteps === totalSteps,
    };
  }, [userV3]);

  if (hideComponent) {
    return null;
  }

  return (
    <Link to='profile/personal-info'>
      <div className={styles.profileSetupContainer}>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progressPercentage}%` }} />
        </div>

        <div className={styles.profileSetupTitle}>
          <div className={styles.title}>{translations.profile_setup}</div>
          <div className={styles.arrow} />
        </div>

        <div className={styles.progressMessage}>
          {remainingSteps === 1
            ? translations.complete_1_more_step
            : translations.complete_2_more_steps}
        </div>
      </div>
    </Link>
  );
};

export default ProfileSetupProgress;
